import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { FiCalendar, FiTrendingUp, FiZap } from "react-icons/fi";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";
import { SelectionContext } from "../../Context/Store";
import {
  getBillingPlans,
  getBillingTransactions,
  getTopupPacks,
} from "../../api/context/billing";
import { buyTopupPack, subscribe } from "./checkout";
import CancelSubscriptionModal from "./components/CancelSubscriptionModal";
import TransactionTable from "./components/TransactionTable";
import PlanCard from "./components/PlanCard";
import TopupCard from "./components/TopupCard";
import PlanSwitchConfirmModal from "./components/PlanSwitchConfirmModal";
import TrialClaimBanner from "./components/TrialClaimBanner";

const DashboardView = ({ balance }) => {
  const { billingBalance, refreshBillingBalance } = useContext(SelectionContext);
  const activeBalance = balance || billingBalance;

  const [showCancel, setShowCancel] = useState(false);
  const [cancelMode, setCancelMode] = useState("current");
  const [confirmPlan, setConfirmPlan] = useState(null);
  const plansRef = useRef(null);
  const topupRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [processingPack, setProcessingPack] = useState(null);

  useEffect(() => {
    Promise.all([getBillingPlans(), getTopupPacks()])
      .then(([plansRes, packsRes]) => {
        if (plansRes?.data?.success) setPlans(plansRes.data.data || []);
        if (packsRes?.data?.success) setPacks(packsRes.data.data || []);
      })
      .finally(() => setPlansLoading(false));
  }, []);

  useEffect(() => {
    setRecentLoading(true);
    getBillingTransactions({ page: 1, pageSize: 10 })
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data;
          const list = Array.isArray(data)
            ? data
            : data?.transactions || data?.rows || [];
          setRecent(list);
        }
      })
      .finally(() => setRecentLoading(false));
  }, [activeBalance]);

  const handleAskConfirm = (plan) => setConfirmPlan(plan);

  const handleConfirmSubscribe = async () => {
    if (!confirmPlan) return;
    setProcessingPlan(confirmPlan.code);
    await subscribe(confirmPlan.code, confirmPlan.name, {
      onSuccess: () => {
        refreshBillingBalance();
        setConfirmPlan(null);
      },
      onDismiss: () => setProcessingPlan(null),
    });
    setProcessingPlan(null);
  };

  const openCancelCurrent = () => {
    setCancelMode("current");
    setShowCancel(true);
  };

  const openCancelUpgrade = () => {
    setCancelMode("upgrade");
    setShowCancel(true);
  };

  const handleBuy = async (pack) => {
    setProcessingPack(pack.code);
    await buyTopupPack(pack.code, {
      onSuccess: () => refreshBillingBalance(),
      onDismiss: () => setProcessingPack(null),
    });
    setProcessingPack(null);
  };

  const subscription = activeBalance?.subscription;
  const wallet = activeBalance?.wallet;
  const totalAvailable = activeBalance?.totalAvailable ?? 0;
  const walletBalance = wallet?.balance ?? 0;
  const scheduledNextPlan = activeBalance?.scheduledNextPlan;
  const trial = activeBalance?.trial;
  const trialAvailable = trial?.available === true;
  const subscriptionHasBalance =
    subscription?.status === "active" && (subscription?.remaining ?? 0) > 0;
  const showTopup = !subscriptionHasBalance;

  const usedPct =
    subscription && subscription.quota > 0
      ? Math.min(100, (subscription.used / subscription.quota) * 100)
      : 0;

  const renewsOn = subscription?.currentPeriodEnd
    ? moment(subscription.currentPeriodEnd).format("MMM D, YYYY")
    : null;

  const scheduledStartOn = scheduledNextPlan?.scheduledStartAt
    ? moment(scheduledNextPlan.scheduledStartAt).format("MMM D, YYYY")
    : null;

  const sortedPlans = [...plans].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );
  const sortedPacks = [...packs].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  return (
    <div className="billing-page">
      <style>{`
        /* Kill extra horizontal padding on the billing wrapper */
        .billing-page .billing-dashboard {
          padding-left: 0 !important;
          padding-right: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 24px !important;
        }

        /* Font-size reductions */
        .billing-page .page-header-sticky h3 { font-size: 18px !important; }
        .billing-page .page-header-sticky p { font-size: 12px !important; }

        .billing-page .billing-hero-plan-name {
          font-size: 11px !important;
          padding: 4px 10px !important;
        }
        .billing-page .billing-hero-plan-name-date { font-size: 10px !important; }
        .billing-page .billing-hero-plan-status { font-size: 10px !important; }
        .billing-page .billing-hero-plan-cancel { font-size: 9px !important; padding: 3px 8px !important; }
        .billing-page .billing-hero-btn {
          font-size: 11px !important;
          padding: 6px 12px !important;
          border-radius: 6px !important;
        }
        .billing-page .billing-hero-number { font-size: 48px !important; }
        .billing-page .billing-hero-label { font-size: 11px !important; }
        .billing-page .billing-hero-meta { font-size: 11px !important; }

        .billing-page .billing-stat-label { font-size: 11px !important; }
        .billing-page .billing-stat-value { font-size: 18px !important; }
        .billing-page .billing-stat-unit { font-size: 11px !important; }
        .billing-page .billing-stat-desc { font-size: 11px !important; }

        .billing-page .billing-section-block-head h5 { font-size: 14px !important; }
        .billing-page .billing-section-block-head p { font-size: 11px !important; }

        .billing-page .billing-card-badge { font-size: 9px !important; }
        .billing-page .billing-card-name { font-size: 13px !important; }
        .billing-page .billing-card-sub { font-size: 11px !important; }
        .billing-page .billing-card-price { font-size: 18px !important; }
        .billing-page .billing-card-period { font-size: 11px !important; }
        .billing-page .billing-card-quota-main { font-size: 12px !important; }
        .billing-page .billing-card-quota-sub { font-size: 10px !important; }
        .billing-page .billing-feature-list li { font-size: 11px !important; }
        .billing-page .billing-card-cta { font-size: 12px !important; }

        .billing-page .billing-topup-badge { font-size: 9px !important; }
        .billing-page .billing-topup-count-num { font-size: 20px !important; }
        .billing-page .billing-topup-count-unit { font-size: 11px !important; }
        .billing-page .billing-topup-price { font-size: 13px !important; }
        .billing-page .billing-topup-per { font-size: 10px !important; }
        .billing-page .billing-topup-cta { font-size: 11px !important; }

        .billing-page .billing-activity-header h5 { font-size: 14px !important; }
        .billing-page .billing-activity-link { font-size: 11px !important; }

        .billing-page .billing-txn-table th { font-size: 10px !important; }
        .billing-page .billing-txn-table td { font-size: 11px !important; }
        .billing-page .billing-txn-badge { font-size: 10px !important; }

        /* Distinct light background for the plans section only */
        .billing-page .billing-plans-section {
          background: #f0fdfa !important;
          border: 1px solid #ccfbf1 !important;
          border-radius: 12px !important;
          padding: 18px !important;
        }

        .billing-page .billing-trial-eyebrow { font-size: 10px !important; }
        .billing-page .billing-trial-title { font-size: 15px !important; }
        .billing-page .billing-trial-sub { font-size: 11px !important; }
        .billing-page .billing-trial-btn { font-size: 12px !important; }

        /* Mobile — strip outer section containers, keep inner cards, horizontally scroll plans/topups */
        @media (max-width: 767.98px) {
          .billing-page .billing-hero-card {
            padding: 18px 16px !important;
            margin-bottom: 28px !important;
            border-radius: 14px !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }
          /* Hoist card-top's children up so we can reorder them individually */
          .billing-page .billing-hero-card-top { display: contents !important; }
          .billing-page .billing-hero-card-plan {
            order: 1;
            justify-content: flex-start !important;
            gap: 8px !important;
          }
          .billing-page .billing-hero-card-count {
            order: 2;
            text-align: center !important;
            margin-bottom: 0 !important;
          }
          .billing-page .billing-hero-card-bottom {
            order: 3;
            gap: 10px !important;
          }
          .billing-page .billing-hero-card-actions {
            order: 4;
            display: flex !important;
            width: 100% !important;
            gap: 8px !important;
            flex-wrap: nowrap !important;
            margin-top: 4px !important;
          }
          .billing-page .billing-hero-card-actions .billing-hero-btn {
            flex: 1 1 0 !important;
            padding: 8px 10px !important;
            font-size: 11px !important;
            text-align: center;
            border-radius: 8px !important;
          }
          .billing-page .billing-hero-number { font-size: 38px !important; }
          .billing-page .billing-hero-label { font-size: 12px !important; opacity: 0.9; }
          .billing-page .billing-hero-meta {
            flex-direction: column !important;
            gap: 6px !important;
            font-size: 11px !important;
          }
          .billing-page .billing-trial-banner {
            padding: 14px 16px !important;
            margin-bottom: 28px !important;
            border-radius: 12px !important;
          }
          .billing-page .billing-trial-title { font-size: 14px !important; }
          .billing-page .billing-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 10px !important;
            margin-bottom: 28px !important;
          }
          .billing-page .billing-stat-card {
            padding: 12px !important;
            border-radius: 10px !important;
          }
          .billing-page .billing-stat-head {
            gap: 6px !important;
            margin-bottom: 10px !important;
          }
          .billing-page .billing-stat-icon {
            width: 22px !important;
            height: 22px !important;
            border-radius: 6px !important;
            font-size: 11px !important;
          }
          .billing-page .billing-stat-icon svg { width: 11px !important; height: 11px !important; }
          .billing-page .billing-stat-label { font-size: 10px !important; }
          .billing-page .billing-stat-value { font-size: 16px !important; }
          .billing-page .billing-stat-unit { font-size: 10px !important; }
          .billing-page .billing-stat-desc { font-size: 10px !important; line-height: 1.4; }

          /* Remove outer container styling on section blocks and activity card */
          .billing-page .billing-section-block,
          .billing-page .billing-activity-card {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin-bottom: 32px !important;
            border-radius: 0 !important;
          }
          .billing-page .billing-activity-card { margin-bottom: 0 !important; }
          /* Plans section: light bg + top/bottom borders + full-width bleed */
          .billing-page .billing-plans-section {
            background: #f0fdfa !important;
            border-top: 1px solid #ccfbf1 !important;
            border-bottom: 1px solid #ccfbf1 !important;
            border-left: none !important;
            border-right: none !important;
            border-radius: 0 !important;
            padding: 16px 0 !important;
            width: 100vw !important;
            max-width: 100vw !important;
            margin-left: calc(50% - 50vw) !important;
            margin-right: calc(50% - 50vw) !important;
            margin-bottom: 16px !important;
          }
          /* Topup section: no background, still full-width bleed for the scroll strip */
          .billing-page .billing-topup-section {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            width: 100vw !important;
            max-width: 100vw !important;
            margin-left: calc(50% - 50vw) !important;
            margin-right: calc(50% - 50vw) !important;
            margin-bottom: 20px !important;
          }
          .billing-page .billing-plans-section .billing-section-block-head,
          .billing-page .billing-topup-section .billing-section-block-head {
            padding: 0 16px !important;
            margin-bottom: 18px !important;
          }
          /* Scroll strip: centered snap so cards snap to viewport center */
          .billing-page .billing-plans-section .billing-plans-grid,
          .billing-page .billing-topup-section .billing-topup-grid {
            padding: 24px calc((100vw - 82vw) / 2) !important;
            scroll-padding-left: calc((100vw - 82vw) / 2) !important;
            scroll-padding-right: calc((100vw - 82vw) / 2) !important;
          }
          .billing-page .billing-plans-section .billing-plans-grid > *,
          .billing-page .billing-topup-section .billing-topup-grid > * {
            scroll-snap-align: center !important;
          }
          /* Match topup card width to plans (82%) */
          .billing-page .billing-topup-grid > * {
            flex: 0 0 82% !important;
            max-width: 82% !important;
          }
          .billing-page .billing-section-block-head,
          .billing-page .billing-activity-header {
            padding: 0 !important;
            margin-bottom: 12px !important;
          }

          /* Horizontally scroll the plan cards — full card visible, edge padding for breathing room */
          .billing-page .billing-plans-grid {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            gap: 12px !important;
            padding: 16px 12px 12px 0 !important;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .billing-page .billing-plans-grid::-webkit-scrollbar { display: none; }
          .billing-page .billing-plans-grid > * {
            flex: 0 0 82% !important;
            max-width: 82% !important;
            scroll-snap-align: start;
          }

          /* Horizontally scroll the topup cards — same padding pattern */
          .billing-page .billing-topup-grid {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
            gap: 12px !important;
            padding: 16px 12px 12px 0 !important;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .billing-page .billing-topup-grid::-webkit-scrollbar { display: none; }
          .billing-page .billing-topup-grid > * {
            flex: 0 0 55% !important;
            max-width: 55% !important;
            scroll-snap-align: start;
          }

          .billing-page .billing-card { padding: 12px !important; }
          .billing-page .billing-card-price { font-size: 16px !important; }
          .billing-page .billing-topup-card { padding: 12px !important; }
          .billing-page .billing-txn-table th,
          .billing-page .billing-txn-table td { padding: 8px 10px !important; }
        }
      `}</style>

      <div className="page-header-sticky">
        <h3 className="fw-bold">Billing & AI photoshoots</h3>
        <p>Manage your subscription and top-up credits.</p>
      </div>

      <div className="billing-dashboard">
        {trialAvailable && <TrialClaimBanner trial={trial} />}

      <div className="billing-hero-card">
        <div className="billing-hero-card-top">
          <div className="billing-hero-card-plan">
            {subscription ? (
              <>
                <span className="billing-hero-plan-name">
                  {subscription.planName} plan
                </span>
                {scheduledNextPlan && (
                  <>
                    <HiOutlineArrowsRightLeft className="billing-hero-plan-arrow" />
                    <span className="billing-hero-plan-name is-next">
                      {scheduledNextPlan.planName} plan
                      {scheduledStartOn && (
                        <span className="billing-hero-plan-name-date">
                          {" · "}
                          {scheduledStartOn}
                        </span>
                      )}
                    </span>
                  </>
                )}
                <span className="billing-hero-plan-status">Active</span>
                {subscription.cancelAtPeriodEnd && renewsOn && (
                  <span className="billing-hero-plan-cancel">
                    Ends {renewsOn}
                  </span>
                )}
              </>
            ) : (
              <span className="billing-hero-plan-name is-empty">
                No active plan
              </span>
            )}
          </div>
          <div className="billing-hero-card-actions">
            <button
              type="button"
              className="billing-hero-btn"
              onClick={() => scrollTo(plansRef)}
            >
              {subscription ? "Manage plan" : "Choose a plan"}
            </button>
            {showTopup && (
              <button
                type="button"
                className="billing-hero-btn"
                onClick={() => scrollTo(topupRef)}
              >
                {subscription ? "Add top-up" : "Buy credits"}
              </button>
            )}
            {subscription && !subscription.cancelAtPeriodEnd && !scheduledNextPlan && (
              <button
                type="button"
                className="billing-hero-btn"
                onClick={openCancelCurrent}
              >
                Cancel plan
              </button>
            )}
          </div>
        </div>

        <div className="billing-hero-card-count">
          <div className="billing-hero-number">{totalAvailable}</div>
          <div className="billing-hero-label">credits available</div>
        </div>

        <div className="billing-hero-card-bottom">
          {subscription ? (
            <>
              <div className="billing-hero-progress">
                <div
                  className="billing-hero-progress-fill"
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <div className="billing-hero-meta">
                <span>
                  <FiTrendingUp />
                  {subscription.used ?? 0} of {subscription.quota ?? 0} used
                  this month
                </span>
                {renewsOn && (
                  <span>
                    <FiCalendar />
                    {subscription.cancelAtPeriodEnd
                      ? `Ends on ${renewsOn}`
                      : `Renews on ${renewsOn}`}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="billing-hero-meta billing-hero-meta-empty">
              <span>
                <FiTrendingUp />
                {walletBalance > 0
                  ? `${walletBalance} top-up credit${walletBalance === 1 ? "" : "s"} in your wallet`
                  : "Subscribe to a plan for monthly credits — or buy top-ups anytime."}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="billing-stats-grid">
        <div className="billing-stat-card">
          <div className="billing-stat-head">
            <div className="billing-stat-icon">
              <FiZap />
            </div>
            <div className="billing-stat-label">Top-up wallet</div>
          </div>
          <div className="billing-stat-value">
            {walletBalance}
            <span className="billing-stat-unit">
              credit{walletBalance === 1 ? "" : "s"}
            </span>
          </div>
          <div className="billing-stat-desc">
            <span className="d-none d-md-inline">
              Never expires. Activates once your monthly quota is used.
            </span>
            <span className="d-inline d-md-none">
              Never expires. Activates after your monthly quota ends.
            </span>
          </div>
        </div>

        {subscription ? (
          <div className="billing-stat-card">
            <div className="billing-stat-head">
              <div className="billing-stat-icon">
                <FaWandMagicSparkles />
              </div>
              <div className="billing-stat-label">This month</div>
            </div>
            <div className="billing-stat-value">
              {subscription.used ?? 0}
              <span className="billing-stat-unit d-none d-md-inline">
                of {subscription.quota ?? 0} used
              </span>
              <span className="billing-stat-unit d-inline d-md-none">
                / {subscription.quota ?? 0}
              </span>
            </div>
            <div className="billing-stat-desc">
              <span className="d-none d-md-inline">
                {subscription.remaining ?? 0} credits remaining in your{" "}
                {subscription.planName} plan
                {renewsOn && !subscription.cancelAtPeriodEnd && (
                  <>. Resets on {renewsOn}.</>
                )}
              </span>
              <span className="d-inline d-md-none">
                {subscription.remaining ?? 0} left in {subscription.planName}
                {renewsOn && !subscription.cancelAtPeriodEnd && (
                  <> · Resets {moment(subscription.currentPeriodEnd).format("MMM D")}</>
                )}
              </span>
            </div>
          </div>
        ) : (
          <div className="billing-stat-card">
            <div className="billing-stat-head">
              <div className="billing-stat-icon">
                <FaWandMagicSparkles />
              </div>
              <div className="billing-stat-label">Monthly plan</div>
            </div>
            <div className="billing-stat-value">
              0
              <span className="billing-stat-unit d-none d-md-inline">no plan yet</span>
              <span className="billing-stat-unit d-inline d-md-none">no plan</span>
            </div>
            <div className="billing-stat-desc">
              <span className="d-none d-md-inline">
                Subscribe to a plan below to get monthly credits at a lower
                per-listing cost.
              </span>
              <span className="d-inline d-md-none">
                Subscribe below for monthly credits at discounted rate.
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="billing-section-block billing-plans-section" ref={plansRef}>
        <div className="billing-section-block-head">
          <h5>{subscription ? "Change your plan" : "Choose a plan"}</h5>
          <p>Monthly quota, resets every billing cycle.</p>
        </div>
        {plansLoading && !plans.length ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="billing-plans-grid">
            {sortedPlans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                isCurrent={subscription?.planCode === plan.code}
                isScheduled={scheduledNextPlan?.planCode === plan.code}
                scheduledStartDate={scheduledStartOn}
                isProcessing={processingPlan === plan.code}
                onSubscribe={handleAskConfirm}
                onCancelUpgrade={openCancelUpgrade}
                hidePopular={!!subscription}
              />
            ))}
          </div>
        )}
      </div>

      {showTopup && (
        <div className="billing-section-block billing-topup-section" ref={topupRef}>
          <div className="billing-section-block-head">
            <h5>
              <FiZap /> Or refill instantly
            </h5>
            <p>
              Top-up credits never expire and activate after your monthly
              quota is used.
            </p>
          </div>
          {plansLoading && !packs.length ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div className="billing-topup-grid">
              {sortedPacks.map((pack) => (
                <TopupCard
                  key={pack.code}
                  pack={pack}
                  isProcessing={processingPack === pack.code}
                  onBuy={handleBuy}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="billing-activity-card">
        <div className="billing-activity-header">
          <h5>Recent activity</h5>
          <Link to="/billing/history" className="billing-activity-link">
            View all →
          </Link>
        </div>
        <TransactionTable rows={recent} loading={recentLoading} />
      </div>

      <CancelSubscriptionModal
        isOpen={showCancel}
        toggle={() => setShowCancel(false)}
        subscription={subscription}
        scheduledNextPlan={scheduledNextPlan}
        mode={cancelMode}
      />

      <PlanSwitchConfirmModal
        isOpen={!!confirmPlan}
        toggle={() => setConfirmPlan(null)}
        targetPlan={confirmPlan}
        currentSubscription={subscription}
        scheduledNextPlan={scheduledNextPlan}
        onConfirm={handleConfirmSubscribe}
        isProcessing={!!processingPlan}
      />
      </div>
    </div>
  );
};

export default DashboardView;
